import { Fragment, useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, tokens } from './lib/api';
import { clearCache, readRefsCache, writeRefsCache } from './lib/cached';
import type { DriverRow, Me, PickupPointRow, ZoneRow } from './lib/types';
import { Login } from './screens/Login';
import { ChangePassword } from './screens/ChangePassword';
import { Orders } from './screens/Orders';
import { Contacts } from './screens/Contacts';
import { AuditLog } from './screens/AuditLog';
import { Exceptions } from './screens/Exceptions';
import { Analytics } from './screens/Analytics';
import { Overview } from './screens/Overview';
import { Drivers } from './screens/Drivers';
import { IntegrationJobs } from './screens/IntegrationJobs';
import { Settlements } from './screens/Settlements';
import { Shifts } from './screens/Shifts';
import { Settings } from './screens/Settings';
import { Pricing } from './screens/Pricing';
import { Suspense, lazy } from 'react';
import { setServerMap } from './lib/mapStyle';
import { CodConfirmDialog, type CodPendingState } from './components/CodConfirmDialog';
import { useRouteSegment } from './lib/route';
import {
  Banknote,
  Bike,
  Calculator,
  CalendarDays,
  ClipboardList,
  Contact,
  FileSearch,
  LayoutDashboard,
  Map as MapIcon,
  Menu,
  Radio,
  RefreshCw,
  Settings as SettingsIcon,
  TrendingUp,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import { UpdateCheck } from './components/UpdateCheck';
import { CodStats } from './screens/CodStats';
import { startLiveUpdate } from './lib/liveUpdate';

// 地图库 250KB —— 懒加载,不看地图的调度员不用等它
const MapOverview = lazy(() =>
  import('./screens/MapOverview').then((m) => ({ default: m.MapOverview })),
);

type Nav =
  | 'overview'
  | 'orders'
  | 'contacts'
  | 'exceptions'
  | 'map'
  | 'drivers'
  | 'audit'
  | 'settings'
  | 'integrations'
  | 'settlements'
  | 'cod'
  | 'shifts'
  | 'pricing'
  | 'analytics';

/**
 * 每一屏叫什么。
 *
 * ★ 手机上侧栏是收起来的,那一列高亮不在了 —— 顶栏得替它说
 *   「你现在在看哪一屏」。菜单和顶栏共用这一份,
 *   分两处写的话改了一处就会对不上。
 */
const NAV_LABEL: Record<Nav, string> = {
  overview: '概览',
  analytics: '分析',
  orders: '订单',
  exceptions: '异常',
  map: '地图',
  contacts: '收件人',
  drivers: '司机',
  shifts: '排班',
  audit: '审计',
  integrations: '推送',
  settlements: '结算',
  cod: '代收款',
  pricing: '计价',
  settings: '设置',
};

export function App() {
  /* 热更新:挂上了就说明这个包能跑。放在最前面 —— 登录页也要能更新 */
  useEffect(() => {
    void startLiveUpdate();
  }, []);
  const [authed, setAuthed] = useState(() => Boolean(tokens.get()));
  /*
   * ★ 「必须先改初始密码」不是掉线。
   *
   *   服务端对这种状态回的是 401 + `PASSWORD_CHANGE_REQUIRED`,
   *   而原来这里把所有 401 一律当成掉线清 token ——
   *   表现是**登录成功之后立刻被弹回登录页**,看起来像密码错了。
   *   每一个新部署的第一次登录都会撞上。
   */
  const [mustChangePassword, setMustChange] = useState(false);
  /** 刚改完密码 —— 登录页要说清楚为什么又要登一次 */
  const [justChanged, setJustChanged] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  // 概览做落地页 —— 调度员上来第一眼该看到「今天什么形状」
  /*
   * ★ 当前这一屏写进地址栏 —— 刷新之后还在原地。
   *   概览做落地页:第一次进来(地址栏里什么都没有)时看到的是它。
   */
  const [navRaw, setNav] = useRouteSegment(0, 'overview');
  const nav = navRaw as Nav;
  /** 手机上的菜单抽屉开着没有(桌面上侧栏一直在,这个值不起作用) */
  const [drawer, setDrawer] = useState(false);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [points, setPoints] = useState<PickupPointRow[]>([]);

  /*
   * ★ 这三样先拿缓存顶上,再去要新的。
   *
   *   司机、区域、取货点是**引用数据**:几乎不变,而每次打开页面
   *   都要等它们回来才有得看(筛选框是空的、建单弹窗的取货点是空的)。
   *   陈旧的代价也最小 —— 多一个昨天离职的司机,在下拉框里而已;
   *   而真去派给他,后端的 driverIsActive 会挡住。
   *
   *   订单那种实时数据不走这条路,各屏自己按需要决定(见 lib/cached.ts)。
   */
  const loadRefs = useCallback(async () => {
    const fromCache = await readRefsCache<{
      drivers: DriverRow[];
      zones: ZoneRow[];
      points: PickupPointRow[];
    }>();
    if (fromCache) {
      setDrivers(fromCache.drivers);
      setZones(fromCache.zones);
      setPoints(fromCache.points);
    }
    try {
      const [d, z, p] = await Promise.all([
        api<DriverRow[]>('/drivers'),
        api<ZoneRow[]>('/zones'),
        api<PickupPointRow[]>('/pickup-points'),
      ]);
      setDrivers(d);
      setZones(z);
      setPoints(p);
      void writeRefsCache({ drivers: d, zones: z, points: p });
    } catch {
      /* 权限不足时静默 —— 页面按权限渲染 */
    }
  }, []);

  /*
   * ★ 手动刷新。打成 App 之后没有浏览器的刷新键 —— 订单、司机这些页
   *   不会自己轮询,调度在手机上看到的就一直是打开那一刻的数据。
   *
   *   做法是让当前这一屏**重新挂载**(换个 key):每一屏的加载逻辑本来就写在
   *   挂载时,不用一屏一屏去接「刷新」事件。效果和浏览器里按 F5 一样 ——
   *   筛选条件也会清掉(它们本来就只在内存里),但顶栏、登录态不动,也不白屏。
   */
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    void loadRefs();
  }, [loadRefs]);

  useEffect(() => {
    if (!authed || mustChangePassword) return;
    void (async () => {
      try {
        const m = await api<Me>('/auth/me');
        // ★ 底图配置随 me 下发 —— 换底图不必重新构建前端
        setServerMap(m.tenant.tiles ?? null);
        setMe(m);
        await loadRefs();
      } catch (e) {
        if (e instanceof ApiFailure && e.code === 'PASSWORD_CHANGE_REQUIRED') {
          // token 是好的,只是这个账号还没改初始密码 —— 别清它
          setMustChange(true);
          return;
        }
        if (e instanceof ApiFailure && e.status === 401) {
          tokens.clear();
          setAuthed(false);
        }
      }
    })();
  }, [authed, mustChangePassword, loadRefs]);

  /*
   * ★ 司机说交上来了、后台还没确认的现金 —— 打开页面就问一次。
   *
   *   用户要的:「管理端那边打开页面的时候就会提示 xxx 司机给了哪些订单的多少钱,
   *   是否确认」,「下次再说就等刷新页面后再提示」。
   *
   * ★ 所以「下次再说」只改这一个 state,不往服务端写、也不记 localStorage ——
   *   刷新页面本来就会重新拉一次,而那正是他要的「下次再提示」。
   *   记到本地的话,他清了缓存或者换台电脑就再也不提示了,
   *   而那笔钱还没对上。
   *
   * ★ 这个钩子必须在下面那几个早返回**之前** —— 放在后面的话,
   *   登录页和正常页面的 hook 数量不一样,整个应用白屏。
   *   (司机端那边为这件事白过三次屏。)
   *
   * ★ 只有 settlement:manage 才拉:调度员没有这个权限,拉了也是 403,
   *   而一个每次打开都在控制台里报错的请求会盖住真正的错误。
   *
   * ★ 请求失败什么都不做:提醒是锦上添花,派单才是本职。
   */
  const [codPending, setCodPending] = useState<CodPendingState | null>(null);
  useEffect(() => {
    if (!me?.permissions.includes('settlement:manage')) return;
    void (async () => {
      try {
        const r = await api<CodPendingState>('/settlements/cod-handovers');
        if (r.due) setCodPending(r);
      } catch {
        // 静默 —— 见上面那段
      }
    })();
  }, [me]);

  if (!authed) {
    return (
      <Login
        notice={justChanged ? '密码已修改,请用新密码登录' : null}
        onDone={(mustChange) => {
          setJustChanged(false);
          setMustChange(mustChange);
          setAuthed(true);
        }}
      />
    );
  }

  if (mustChangePassword) {
    /*
     * ★ 改完密码必须重新登录 —— 这不是偷懒。
     *   后端改密时把 permVersion +1,**所有旧 token 当场失效**
     *   (包括手上这个),这样别的设备上还开着的会话会被踢掉。
     *   所以这里不能直接进系统:那样每个请求都会 401,
     *   而用户看到的会是「改密成功了,然后系统坏了」。
     */
    return (
      <ChangePassword
        onDone={() => {
          tokens.clear();
          setMustChange(false);
          setJustChanged(true);
          setAuthed(false);
        }}
      />
    );
  }

  const can = (p: string) => me?.permissions.includes(p) ?? false;

  return (
    <div className="min-h-dvh flex bg-slate-50 dark:bg-slate-900">
      {/*
        ★ 盖在所有东西上面(z-80),包括详情抽屉。
          这是钱,不是一条可以待会儿再看的通知。
      */}
      {codPending && (
        <CodConfirmDialog
          state={codPending}
          onLater={() => setCodPending(null)}
          onDone={() => setCodPending(null)}
        />
      )}
      {/*
        ★ 手机上侧栏收起来。
    
          224px 的侧栏在 390px 宽的手机上吃掉 **57%** ——
          剩下的地方连「订单」两个字都放不下(标题被挤成竖排),
          按钮变成一列单字。调度在路上用手机看一眼订单是常事,
          而这一屏在手机上等于打不开。
    
          用抽屉不用底部标签栏:这里有十来个入口,底部栏塞不下,
          挤成小图标之后谁也认不出「排班」和「审计」哪个是哪个。
      */}
      {drawer && (
        <button
          aria-label="关闭菜单"
          onClick={() => setDrawer(false)}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      <aside
        // 抽屉从屏幕顶到底,App 里要让开刘海和底部横条(浏览器里两个都是 0)
        className={`fixed inset-y-0 left-0 z-40 w-56 shrink-0 border-r border-slate-200
                    pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]
                    dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col
                    transition-transform lg:sticky lg:top-0 lg:bottom-auto lg:h-dvh lg:translate-x-0
                    ${drawer ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="font-semibold text-slate-900 dark:text-slate-100">
            {me?.tenant.name ?? '配送管理'}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{me?.user.name}</div>
        </div>

        {/*
          ★ 给主菜单一个名字。设置页里还有一个「设置分类」侧栏,
            两个 nav 同时在页面上时,「找那个导航」就成了歧义 ——
            读屏念出来也是两个都叫「导航」。
        */}
        {/*
          ★ 菜单自己滚(min-h-0 + overflow-y-auto)。十几个入口在矮屏上放不下,
            之前侧栏没有滚动区:手机抽屉里「设置」被截在屏幕外,一划滚的是后面的页面;
            电脑上侧栏跟着页面一起动。现在侧栏粘在视口里、菜单在里面滚,
            overscroll-contain 让滚到头时不带着后面的页面跑。
        */}
        <nav
          aria-label="主菜单"
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-1"
        >
          {(
            [
              ['overview', '概览', LayoutDashboard, 'order:read'],
              // ★ 紧跟概览:概览回答「今天怎么样」,这里回答「这段时间怎么样」
              ['analytics', '分析', TrendingUp, 'analytics:read'],
              ['orders', '订单', ClipboardList, 'order:read'],
              // 异常排第二 —— 调度员每天第一件事是看「有什么不对」
              ['exceptions', '异常', TriangleAlert, 'order:read'],
              ['map', '地图', MapIcon, 'order:read'],
              ['contacts', '收件人', Contact, 'contact:read'],
              ['drivers', '司机', Bike, 'driver:read'],
              ['shifts', '排班', CalendarDays, 'shift:read'],
              ['audit', '审计', FileSearch, 'audit:read'],
              // 只有配了三方对接的租户才看得到 —— 没接三方的人不该多一个空菜单
              ['integrations', '推送', Radio, 'integration:read'],
              ['settlements', '结算', Wallet, 'settlement:read'],
              // 全店的现金账 —— 管理员和财务看;司机(只看自己)和调度看不到
              ['cod', '代收款', Banknote, 'settlement:manage'],
              ['pricing', '计价', Calculator, 'pricing:read'],
              ['settings', '设置', SettingsIcon, 'zone:read'],
            ] as const
          )
            // 前端隐藏只是体验,不是安全边界 —— 后端会再校验一次
            .filter(([, , , perm]) => can(perm))
            .map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => {
                  setNav(key);
                  // 手机上点完就收起来 —— 不然抽屉挡着他刚要看的东西
                  setDrawer(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition ${
                  nav === key
                    ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 font-medium'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                <Icon className="size-4 shrink-0" />
                {label}
              </button>
            ))}
        </nav>

        <UpdateCheck />
        <button
          onClick={() => {
            tokens.clear();
            /*
             * ★ 缓存里有几千个收件人的姓名、电话、门牌号 ——
             *   不清的话就留给了下一个在这台电脑上登录的人。
             */
            void clearCache();
            setAuthed(false);
          }}
          className="m-3 px-3 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-50
                     dark:hover:bg-slate-700/50 text-left"
        >
          退出登录
        </button>
      </aside>

      {/*
        ★ **不要**在这儿写 overflow-auto。
 
          写了之后 `<main>` 就成了「最近的滚动祖先」,而它的高度是跟着内容长的
          —— 它自己**永远不滚**,真正在滚的是文档。于是它里面每一个 `sticky`
          都失效:粘的是一个不会动的盒子。
 
          量出来的样子:main.scrollHeight === main.clientHeight === 2140,
          而 document 是 2140 / 900。往下滑 1200 之后,批量操作条的 top 是 **-34**
          —— 滚出屏幕了。用户报的原话:「往下滑选择一些数据后还得滑上去才能选操作」。
 
          顺带坏掉的还有手机顶栏(它也写着 sticky top-0)。
          去掉之后文档成为滚动容器,两条都正常粘住。
          横向溢出由表格自己那层 overflow-x-auto 管,不靠这里。
      */}
      <main className="min-w-0 flex-1">
        {/*
          ★ 手机上的顶栏:一个汉堡 + 当前在哪一屏。
            只放汉堡不写当前位置的话,收起侧栏之后
            人就不知道自己在看什么了 —— 桌面上那一列高亮本来在替他记着。
        */}
        <div
          /*
            ★ 高度写死成 --mobile-bar-h(3rem + 刘海那一截)。批量操作条要粘在**它下面**,
              而那需要一个确定的数字 —— 靠 py-2 加按钮高度算出来的高度
              会随字号、边框变化,而变化之后批量条会和顶栏差出几个像素,
              露出一条会闪的缝。
          */
          className="sticky top-0 z-20 flex h-[var(--mobile-bar-h)] pt-[env(safe-area-inset-top)] items-center gap-3 border-b border-slate-200
                     bg-white px-3 lg:hidden dark:border-slate-700 dark:bg-slate-800"
        >
          <button
            onClick={() => setDrawer(true)}
            aria-label="菜单"
            className="grid h-9 w-9 place-items-center rounded-lg border border-slate-300 text-lg
                       dark:border-slate-600"
          >
            <Menu className="size-5" />
          </button>
          <span className="font-medium text-slate-900 dark:text-slate-100">{NAV_LABEL[nav]}</span>
          <button
            onClick={refresh}
            aria-label="刷新"
            className="ml-auto grid h-9 w-9 place-items-center rounded-lg text-slate-500
                       active:bg-slate-100 dark:text-slate-400 dark:active:bg-slate-700"
          >
            <RefreshCw className="size-5" />
          </button>
        </div>

        <Fragment key={refreshKey}>

          {nav === 'overview' && <Overview onGoExceptions={() => setNav('exceptions')} />}
          {nav === 'analytics' && <Analytics />}
          {nav === 'contacts' && <Contacts permissions={me?.permissions ?? []} />}
          {nav === 'orders' && (
            <Orders
              drivers={drivers}
              zones={zones}
              points={points}
              permissions={me?.permissions ?? []}
              features={me?.tenant.features}
            />
          )}
          {nav === 'exceptions' && (
            <Exceptions drivers={drivers} zones={zones} permissions={me?.permissions ?? []} />
          )}
          {nav === 'map' && (
            <Suspense
              fallback={
                <div className="grid place-items-center h-dvh text-slate-400">地图加载中…</div>
              }
            >
              <MapOverview drivers={drivers} features={me?.tenant.features} />
            </Suspense>
          )}
          {nav === 'drivers' && (
            <Drivers
              drivers={drivers}
              zones={zones}
              permissions={me?.permissions ?? []}
              onChanged={loadRefs}
            />
          )}
          {nav === 'shifts' && <Shifts drivers={drivers} permissions={me?.permissions ?? []} />}
          {nav === 'audit' && <AuditLog />}
          {nav === 'integrations' && <IntegrationJobs permissions={me?.permissions ?? []} />}
          {nav === 'settlements' && (
            <Settlements permissions={me?.permissions ?? []} drivers={drivers} />
          )}
          {nav === 'cod' && (
            <CodStats drivers={drivers} zones={zones} timezone={me?.tenant.timezone ?? 'UTC'} />
          )}
          {nav === 'pricing' && <Pricing zones={zones} permissions={me?.permissions ?? []} />}
          {nav === 'settings' && (
            <Settings
              zones={zones}
              points={points}
              drivers={drivers}
              permissions={me?.permissions ?? []}
              onChanged={loadRefs}
            />
          )}
        </Fragment>
      </main>
    </div>
  );
}
